import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import Toast from "@moltech/molui/toast";

/** SignalR */
import { getHubConnection, startConnection, stopConnection } from "../../utils/signalR/signalRService";

/** RTK Query — invalidate to force refetch of badge count and recent list */
import { notificationApi } from "../services/notificationApi";
import { playNotificationSound } from "../../utils/common/notificationSound";

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Maps the notification type string from the backend to an appropriate
 * toast variant / icon so the toast visually matches the notification severity.
 */
const getToastConfig = (type, title, message) => {
  const truncated =
    message.length > 100 ? `${message.substring(0, 97)}...` : message;

  const base = { summary: title, detail: truncated, life: 6000 };

  switch (type) {
    case "Success":
      return { ...base, severity: "success" };
    case "Warning":
      return { ...base, severity: "warn" };
    case "Error":
      return { ...base, severity: "error" };
    case "ApprovalRequired":
      return { ...base, severity: "warn", summary: `⏳ ${title}` };
    case "Reminder":
      return { ...base, severity: "info", summary: `🔔 ${title}` };
    case "Info":
    default:
      return { ...base, severity: "info" };
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useSignalRNotifications
 *
 * Establishes a SignalR connection to the notification hub when the user is
 * authenticated, listens for "ReceiveNotification" events, and on each event:
 *  1. Invalidates the RTK Query notification cache tags so the badge count and
 *     recent dropdown automatically refetch without a page reload.
 *  2. Shows a toast popup with the notification title and message.
 *
 * Mount this hook once at the top-level authenticated layout (Layouts.js).
 */
const useSignalRNotifications = () => {
  const dispatch = useDispatch();
  const handlerRegistered = useRef(false);

  useEffect(() => {
    let isMounted = true;

    const connect = async () => {
      await startConnection();

      if (!isMounted) return;

      const connection = getHubConnection();

      if (handlerRegistered.current) return;
      handlerRegistered.current = true;

      // Listen for live notification pushes from the backend.
      connection.on("ReceiveNotification", (payload) => {
        console.log("[SignalR Hook] Received notification payload:", payload);
        if (!payload) return;
        playNotificationSound();

        // 1. Invalidate RTK Query cache so badge count, recent list, and full history auto-refresh.
        dispatch(
          notificationApi.util.invalidateTags([
            { type: "NotificationUnreadCount", id: "CURRENT" },
            { type: "NotificationRecent", id: "LIST" },
            { type: "NotificationHistory", id: "LIST" },
          ]),
        );

        // 2. Show a toast popup so the user is alerted even if the dropdown is closed.
        const { severity, summary, detail, life } = getToastConfig(
          payload.type,
          payload.title,
          payload.message,
        );

        switch (severity) {
          case "success":
            Toast.success(summary, detail, life);
            break;
          case "warn":
            Toast.warn(summary, detail, life);
            break;
          case "error":
            Toast.error(summary, detail, life);
            break;
          case "info":
          default:
            Toast.info(summary, detail, life);
            break;
        }
      });

      // Log reconnection events in development for debugging.
      connection.onreconnecting((err) => {
        console.warn("[SignalR] Reconnecting...", err?.message);
      });

      connection.onreconnected((connectionId) => {
        console.info("[SignalR] Reconnected.", connectionId);
      });

      connection.onclose((err) => {
        console.warn("[SignalR] Connection closed.", err?.message);
        handlerRegistered.current = false;
      });
    };

    connect().catch((err) =>
      console.error("[SignalR] Failed to connect:", err),
    );

    return () => {
      isMounted = false;
      // Stop the connection when the layout unmounts (i.e. user logs out).
      stopConnection();
      handlerRegistered.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export default useSignalRNotifications;
